//src/components/Header.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import Navigation from "./Navigation";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { useTheme } from "../contexts/ThemeContext";
import Notification from "./Notification";

// Màu cho avatar tự động tạo
const AVATAR_COLORS = [
  {
    bg: "bg-emerald-200",
    text: "text-emerald-700",
    darkBg: "dark:bg-emerald-700",
    darkText: "dark:text-emerald-100",
  },
  {
    bg: "bg-blue-200",
    text: "text-blue-700",
    darkBg: "dark:bg-blue-700",
    darkText: "dark:text-blue-100",
  },
  {
    bg: "bg-amber-200",
    text: "text-amber-700",
    darkBg: "dark:bg-amber-700",
    darkText: "dark:text-amber-100",
  },
  {
    bg: "bg-pink-200",
    text: "text-pink-700",
    darkBg: "dark:bg-pink-700",
    darkText: "dark:text-pink-100",
  },
  {
    bg: "bg-purple-200",
    text: "text-purple-700",
    darkBg: "dark:bg-purple-700",
    darkText: "dark:text-purple-100",
  },
  {
    bg: "bg-indigo-200",
    text: "text-indigo-700",
    darkBg: "dark:bg-indigo-700",
    darkText: "dark:text-indigo-100",
  },
  {
    bg: "bg-rose-200",
    text: "text-rose-700",
    darkBg: "dark:bg-rose-700",
    darkText: "dark:text-rose-100",
  },
  {
    bg: "bg-cyan-200",
    text: "text-cyan-700",
    darkBg: "dark:bg-cyan-700",
    darkText: "dark:text-cyan-100",
  },
];

// Hàm tạo avatar từ tên người dùng
const generateInitialsAvatar = (user, size = "md") => {
  if (!user) return null;

  // Xác định chữ cái đầu tiên từ tên hiển thị hoặc email
  const initial = user?.displayName?.charAt(0) || user?.email?.charAt(0) || "?";

  // Sử dụng user.uid để chọn màu nhất quán cho mỗi người dùng
  const colorIndex = user.uid
    ? [...user.uid].reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      AVATAR_COLORS.length
    : 0;

  const color = AVATAR_COLORS[colorIndex];

  // Kích thước dựa trên tham số
  const sizeClasses =
    size === "sm"
      ? "w-8 h-8"
      : size === "lg"
      ? "w-12 h-12 text-xl"
      : "w-10 h-10";

  // Hình dạng avatar (tròn hoặc bo góc)
  const shape = size === "sidebar" ? "rounded-lg" : "rounded-full";

  return (
    <div
      className={`${sizeClasses} ${shape} ${color.bg} ${color.text} ${color.darkBg} ${color.darkText} flex items-center justify-center shadow-sm`}
    >
      {initial.toUpperCase()}
    </div>
  );
};

const Header = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  // Lấy dữ liệu người dùng từ Firestore
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Lấy tài liệu người dùng từ Firestore
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            // Kết hợp dữ liệu người dùng từ auth với dữ liệu từ Firestore
            setUserData({
              uid: user.uid,
              email: user.email,
              displayName:
                userSnap.data().displayName || user.displayName || "Người dùng",
              photoURL: user.photoURL || null, // Không dùng ảnh placeholder mặc định nữa
              ...userSnap.data(),
            });
          } else {
            // Sử dụng dữ liệu auth nếu tài liệu Firestore không tồn tại
            setUserData({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || "Người dùng",
              photoURL: user.photoURL || null,
            });
          }
        } catch (error) {
          console.error("Lỗi khi lấy dữ liệu người dùng:", error);
        }
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Lấy thông báo chưa đọc
  const fetchUnreadNotifications = useCallback(async () => {
    if (!auth.currentUser) return;

    try {
      const notificationsQuery = query(
        collection(db, "notifications"),
        where("userId", "==", auth.currentUser.uid),
        where("read", "==", false)
      );

      const querySnapshot = await getDocs(notificationsQuery);
      setUnreadNotifications(querySnapshot.size);
    } catch (error) {
      console.error("Lỗi khi lấy thông báo chưa đọc:", error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadNotifications();

    // Đặt khoảng thời gian để kiểm tra thông báo chưa đọc
    const intervalId = setInterval(fetchUnreadNotifications, 60000); // Kiểm tra mỗi phút

    return () => clearInterval(intervalId);
  }, [fetchUnreadNotifications]);

  // Khi panel thông báo đóng, làm mới thông báo chưa đọc
  useEffect(() => {
    if (!showNotifications) {
      fetchUnreadNotifications();
    }
  }, [showNotifications, fetchUnreadNotifications]);

  // Xử lý click bên ngoài thanh bên để đóng nó
  const handleOutsideClick = (e) => {
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(e.target) &&
      e.target.classList.contains("sidebar-overlay")
    ) {
      setShowSidebar(false);
    }
  };

  // Thêm trình nghe phím ESC để đóng thanh bên
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape" && showSidebar) {
        setShowSidebar(false);
      }
    };

    if (showSidebar) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [showSidebar]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-green-200 dark:bg-gray-800 shadow-sm dark:shadow-gray-700">
      <div className="border-b border-green-300 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                className="p-2 rounded-md bg-emerald-500 hover:bg-emerald-600 cursor-pointer dark:bg-emerald-600 dark:hover:bg-emerald-700"
                onClick={() => setShowSidebar(true)}
                aria-label="Mở menu"
              >
                <div className="w-5 h-0.5 bg-white mb-1"></div>
                <div className="w-5 h-0.5 bg-white mb-1"></div>
                <div className="w-5 h-0.5 bg-white"></div>
              </button>

              {/* Thanh bên với hiệu ứng trượt vào */}
              <div
                className={`fixed inset-y-0 left-0 z-50 flex transition-all duration-300 ease-in-out ${
                  showSidebar ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
              >
                {/* Lớp phủ bán trong suốt */}
                <div
                  className={`fixed inset-0 bg-gray-500 transition-opacity duration-300 ${
                    showSidebar ? "bg-opacity-75" : "bg-opacity-0"
                  } sidebar-overlay dark:bg-gray-900 dark:bg-opacity-75`}
                  onClick={handleOutsideClick}
                ></div>

                {/* Panel thanh bên */}
                <div
                  ref={sidebarRef}
                  className={`relative flex flex-col w-80 max-w-xs bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
                    showSidebar ? "translate-x-0" : "-translate-x-full"
                  } dark:bg-gray-800`}
                >
                  <div className="absolute top-4 right-4">
                    <button
                      className="flex items-center justify-center h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:hover:bg-gray-600"
                      onClick={() => setShowSidebar(false)}
                      aria-label="Đóng thanh bên"
                    >
                      <i className="fas fa-times text-gray-600 text-lg dark:text-gray-300"></i>
                    </button>
                  </div>

                  <div className="flex-1 h-0 pt-6 pb-4 overflow-y-auto">
                    <div className="flex-shrink-0 flex items-center px-6 mb-6">
                      <div className="w-9 h-9 bg-emerald-500 rounded-lg flex items-center justify-center shadow-sm dark:bg-emerald-600">
                        <span className="text-white text-base font-semibold">
                          GW
                        </span>
                      </div>
                      <span className="ml-3 text-xl font-bold text-gray-900 dark:text-white">
                        GreenSchool WorkSpace
                      </span>
                    </div>

                    {/* Thành phần điều hướng */}
                    <Navigation />
                  </div>

                  {/* Thêm liên kết KPI Dashboard vào sidebar */}
                  <div className="px-6 py-3">
                    <NavLink
                      to="/kpi-dashboard"
                      className={({ isActive }) =>
                        `flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-150 ${
                          isActive
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-700 dark:text-white"
                            : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        }`
                      }
                      onClick={() => setShowSidebar(false)} // Đóng sidebar khi click
                    >
                      <i className="fas fa-chart-line mr-3 w-5 text-center"></i>
                      KPI Dashboard
                    </NavLink>
                  </div>

                  {/* Hồ sơ người dùng trong thanh bên - sử dụng dữ liệu Firestore */}
                  <div className="flex-shrink-0 flex border-t border-gray-200 p-6 bg-gray-50 dark:border-gray-700 dark:bg-gray-700">
                    {loading ? (
                      <div className="w-full flex justify-center">
                        <div className="animate-pulse h-10 w-10 bg-gray-200 rounded-full dark:bg-gray-600"></div>
                        <div className="ml-3 flex-1">
                          <div className="animate-pulse h-4 w-24 bg-gray-200 rounded dark:bg-gray-600"></div>
                          <div className="animate-pulse mt-2 h-3 w-16 bg-gray-200 rounded dark:bg-gray-600"></div>
                        </div>
                      </div>
                    ) : userData ? (
                      <div className="flex items-center w-full">
                        <div className="flex-shrink-0">
                          {userData?.photoURL ? (
                            <img
                              className="h-10 w-10 rounded-lg shadow-sm object-cover"
                              src={userData.photoURL}
                              alt={userData.displayName}
                            />
                          ) : (
                            generateInitialsAvatar({ ...userData }, "sidebar")
                          )}
                        </div>
                        <div className="ml-3 flex-1">
                          <p className="text-base font-semibold text-gray-900 dark:text-white">
                            {userData.displayName}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {userData.email}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center w-full">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center dark:bg-gray-600">
                            <i className="fas fa-user text-gray-400 dark:text-gray-300"></i>
                          </div>
                        </div>
                        <div className="ml-3 flex-1">
                          <p className="text-base font-semibold text-gray-900 dark:text-white">
                            Khách
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            Chưa đăng nhập
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="ml-4 flex items-center">
                <img
                  className="h-8 cursor-pointer"
                  src="/image.png"
                  alt="Logo TaskFlow"
                  onClick={() => navigate("/dashboard")}
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative ml-3">
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() => navigate(`/profile/${userData?.uid}`)}
                >
                  {userData?.photoURL ? (
                    <img
                      src={userData.photoURL}
                      alt={userData.displayName || userData.email}
                      className="w-8 h-8 rounded-full object-cover mr-2 border-2 border-white dark:border-gray-700 shadow-sm"
                    />
                  ) : userData ? (
                    generateInitialsAvatar({ ...userData }, "sm")
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 mr-2 dark:bg-gray-600 dark:text-gray-300">
                      <i className="fas fa-user text-sm"></i>
                    </div>
                  )}
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200 hidden md:block">
                    {userData?.displayName || "Khách"}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-gray-400 ml-1 dark:text-gray-300"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>

              <div className="relative">
                <button
                  className="relative hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-full"
                  aria-label="Thông báo"
                  data-notification-toggle
                  onClick={() => setShowNotifications(!showNotifications)}
                >
                  <i className="fas fa-bell text-gray-500 dark:text-gray-300"></i>
                  {unreadNotifications > 0 && (
                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
                      {unreadNotifications > 9 ? "9+" : unreadNotifications}
                    </span>
                  )}
                </button>

                <Notification
                  isOpen={showNotifications}
                  onClose={() => setShowNotifications(false)}
                  onNotificationsRead={fetchUnreadNotifications}
                />
              </div>

              <button
                onClick={() => navigate("/settings")}
                className="hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-full"
                aria-label="Cài đặt"
              >
                <i className="fas fa-cog text-gray-500 dark:text-gray-300"></i>
              </button>

              <button
                onClick={toggleTheme}
                className="hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-full"
                aria-label="Chuyển đổi chế độ sáng/tối"
              >
                {theme === "light" ? (
                  <i className="fas fa-moon text-gray-500 dark:text-gray-400"></i>
                ) : (
                  <i className="fas fa-sun text-yellow-500"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
// //src/components/Header.js
// import React, { useState, useEffect, useRef } from 'react';
// import Navigation from './Navigation';
// import { auth, db } from '../firebase';
// import { onAuthStateChanged } from 'firebase/auth';
// import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
// import { useNavigate } from 'react-router-dom';
// import { useTheme } from '../contexts/ThemeContext';
// import Notification from './Notification';

// const Header = () => {
//   const [showSidebar, setShowSidebar] = useState(false);
//   const [userData, setUserData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const sidebarRef = useRef(null);
//   const navigate = useNavigate();
//   const { theme, toggleTheme } = useTheme();
//   const [showNotifications, setShowNotifications] = useState(false);
//   const [unreadNotifications, setUnreadNotifications] = useState(0);

//   // Fetch user data from Firestore
//   useEffect(() => {
//     const unsubscribe = onAuthStateChanged(auth, async (user) => {
//       if (user) {
//         try {
//           // Get user document from Firestore
//           const userRef = doc(db, "users", user.uid);
//           const userSnap = await getDoc(userRef);

//           if (userSnap.exists()) {
//             // Combine auth user data with Firestore data
//             setUserData({
//               uid: user.uid,
//               email: user.email,
//               displayName: userSnap.data().displayName || user.displayName || 'User',
//               photoURL: user.photoURL || 'https://via.placeholder.com/100',
//               ...userSnap.data()
//             });
//           } else {
//             // Use auth data if Firestore document doesn't exist
//             setUserData({
//               uid: user.uid,
//               email: user.email,
//               displayName: user.displayName || 'User',
//               photoURL: user.photoURL || 'https://via.placeholder.com/100'
//             });
//           }
//         } catch (error) {
//           console.error("Error fetching user data:", error);
//         }
//       } else {
//         setUserData(null);
//       }
//       setLoading(false);
//     });

//     return () => unsubscribe();
//   }, []);

//   // Get unread notifications
//   useEffect(() => {
//     const fetchUnreadNotifications = async () => {
//       if (!auth.currentUser) return;

//       try {
//         const notificationsQuery = query(
//           collection(db, 'notifications'),
//           where('userId', '==', auth.currentUser.uid),
//           where('read', '==', false)
//         );

//         const querySnapshot = await getDocs(notificationsQuery);
//         setUnreadNotifications(querySnapshot.size);
//       } catch (error) {
//         console.error('Error fetching unread notifications:', error);
//       }
//     };

//     fetchUnreadNotifications();

//     // Set interval to check for unread notifications
//     const intervalId = setInterval(fetchUnreadNotifications, 60000); // Check every minute

//     return () => clearInterval(intervalId);
//   }, [auth.currentUser]);

//   // When notifications panel is closed, refresh unread notifications
//   useEffect(() => {
//     if (!showNotifications) {
//       const fetchUnreadNotifications = async () => {
//         if (!auth.currentUser) return;

//         try {
//           const notificationsQuery = query(
//             collection(db, 'notifications'),
//             where('userId', '==', auth.currentUser.uid),
//             where('read', '==', false)
//           );

//           const querySnapshot = await getDocs(notificationsQuery);
//           setUnreadNotifications(querySnapshot.size);
//         } catch (error) {
//           console.error('Error fetching unread notifications:', error);
//         }
//       };

//       fetchUnreadNotifications();
//     }
//   }, [showNotifications, auth.currentUser]);

//   // Handle clicks outside the sidebar to close it
//   const handleOutsideClick = (e) => {
//     if (sidebarRef.current && !sidebarRef.current.contains(e.target) &&
//         e.target.classList.contains('sidebar-overlay')) {
//       setShowSidebar(false);
//     }
//   };

//   // Add ESC key listener to close sidebar
//   useEffect(() => {
//     const handleEscKey = (e) => {
//       if (e.key === 'Escape' && showSidebar) {
//         setShowSidebar(false);
//       }
//     };

//     if (showSidebar) {
//       document.addEventListener('keydown', handleEscKey);
//     }

//     return () => {
//       document.removeEventListener('keydown', handleEscKey);
//     };
//   }, [showSidebar]);

//   return (
//     <header className="fixed top-0 left-0 right-0 z-50 bg-green-200 dark:bg-gray-800 shadow-sm dark:shadow-gray-700">
//       <div className="border-b border-green-300 dark:border-gray-700">
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//           <div className="flex justify-between items-center h-16">
//             <div className="flex items-center">
//               <button
//                 className="p-2 rounded-md bg-emerald-500 hover:bg-emerald-600 cursor-pointer dark:bg-emerald-600 dark:hover:bg-emerald-700"
//                 onClick={() => setShowSidebar(true)}
//                 aria-label="Open sidebar menu"
//               >
//                 <div className="w-5 h-0.5 bg-white mb-1"></div>
//                 <div className="w-5 h-0.5 bg-white mb-1"></div>
//                 <div className="w-5 h-0.5 bg-white"></div>
//               </button>

//               {/* Sidebar with slide-in animation */}
//               <div className={`fixed inset-y-0 left-0 z-50 flex transition-all duration-300 ease-in-out ${showSidebar ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
//                 {/* Semi-transparent overlay */}
//                 <div
//                   className={`fixed inset-0 bg-gray-500 transition-opacity duration-300 ${showSidebar ? 'bg-opacity-75' : 'bg-opacity-0'} sidebar-overlay dark:bg-gray-900 dark:bg-opacity-75`}
//                   onClick={handleOutsideClick}
//                 ></div>

//                 {/* Sidebar panel */}
//                 <div
//                   ref={sidebarRef}
//                   className={`relative flex flex-col w-80 max-w-xs bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showSidebar ? 'translate-x-0' : '-translate-x-full'} dark:bg-gray-800`}
//                 >
//                   <div className="absolute top-4 right-4">
//                     <button
//                       className="flex items-center justify-center h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:hover:bg-gray-600"
//                       onClick={() => setShowSidebar(false)}
//                       aria-label="Close sidebar"
//                     >
//                       <i className="fas fa-times text-gray-600 text-lg dark:text-gray-300"></i>
//                     </button>
//                   </div>

//                   <div className="flex-1 h-0 pt-6 pb-4 overflow-y-auto">
//                     <div className="flex-shrink-0 flex items-center px-6 mb-6">
//                       <div className="w-9 h-9 bg-emerald-500 rounded-lg flex items-center justify-center shadow-sm dark:bg-emerald-600">
//                         <span className="text-white text-base font-semibold">CP</span>
//                       </div>
//                       <span className="ml-3 text-xl font-bold text-gray-900 dark:text-white">Creative Pro</span>
//                     </div>

//                     {/* Navigation component */}
//                     <Navigation />
//                   </div>

//                   {/* Thêm liên kết KPI Dashboard vào sidebar */}
//                   <div className="px-6 py-3">
//                     <NavLink
//                       to="/kpi-dashboard"
//                       className={({ isActive }) =>
//                         `flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-150 ${
//                           isActive
//                             ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-700 dark:text-white'
//                             : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
//                         }`
//                       }
//                       onClick={() => setShowSidebar(false)} // Đóng sidebar khi click
//                     >
//                       <i className="fas fa-chart-line mr-3 w-5 text-center"></i>
//                       KPI Dashboard
//                     </NavLink>
//                   </div>

//                   {/* Hồ sơ người dùng trong thanh bên - sử dụng dữ liệu Firestore */}
//                   <div className="flex-shrink-0 flex border-t border-gray-200 p-6 bg-gray-50 dark:border-gray-700 dark:bg-gray-700">
//                     {loading ? (
//                       <div className="w-full flex justify-center">
//                         <div className="animate-pulse h-10 w-10 bg-gray-200 rounded-full dark:bg-gray-600"></div>
//                         <div className="ml-3 flex-1">
//                           <div className="animate-pulse h-4 w-24 bg-gray-200 rounded dark:bg-gray-600"></div>
//                           <div className="animate-pulse mt-2 h-3 w-16 bg-gray-200 rounded dark:bg-gray-600"></div>
//                         </div>
//                       </div>
//                     ) : userData ? (
//                       <div className="flex items-center w-full">
//                         <div className="flex-shrink-0">
//                           {userData?.photoURL ? (
//                             <img
//                               className="h-10 w-10 rounded-lg shadow-sm object-cover"
//                               src={userData.photoURL}
//                               alt={userData.displayName}
//                             />
//                           ) : (
//                             <div className="h-10 w-10 rounded-lg bg-emerald-200 flex items-center justify-center text-emerald-700 shadow-sm dark:bg-emerald-700 dark:text-emerald-100">
//                               {userData?.displayName?.charAt(0) || userData?.email?.charAt(0) || '?'}
//                             </div>
//                           )}
//                         </div>
//                         <div className="ml-3 flex-1">
//                           <p className="text-base font-semibold text-gray-900 dark:text-white">{userData.displayName}</p>
//                           <p className="text-sm text-gray-600 dark:text-gray-300">{userData.email}</p>
//                         </div>
//                       </div>
//                     ) : (
//                       <div className="flex items-center w-full">
//                         <div className="flex-shrink-0">
//                           <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center dark:bg-gray-600">
//                             <i className="fas fa-user text-gray-400 dark:text-gray-300"></i>
//                           </div>
//                         </div>
//                         <div className="ml-3 flex-1">
//                           <p className="text-base font-semibold text-gray-900 dark:text-white">Guest User</p>
//                           <p className="text-sm text-gray-600 dark:text-gray-300">Not signed in</p>
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>

//               <div className="ml-4 flex items-center">
//                 <img
//                   className="h-8"
//                   src="/image.png"
//                   alt="Logo"
//                 />
//               </div>
//             </div>
//             <div className="flex items-center space-x-4">
//               <div className="relative ml-3">
//                 <div
//                   className="flex items-center cursor-pointer"
//                   onClick={() => navigate(`/profile/${userData?.uid}`)}
//                 >
//                   {userData?.photoURL ? (
//                     <img
//                       src={userData.photoURL}
//                       alt={userData.displayName || userData.email}
//                       className="w-8 h-8 rounded-full object-cover mr-2"
//                     />
//                   ) : (
//                     <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center text-emerald-700 mr-2 dark:bg-emerald-700 dark:text-emerald-100">
//                       {userData?.displayName?.charAt(0) || userData?.email?.charAt(0) || '?'}
//                     </div>
//                   )}
//                   <span className="text-sm font-medium text-gray-700 dark:text-gray-200 hidden md:block">
//                     {userData?.displayName || userData?.email}
//                   </span>
//                   <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 ml-1 dark:text-gray-300" viewBox="0 0 20 20" fill="currentColor">
//                     <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
//                   </svg>
//                 </div>
//               </div>

//               <div className="relative">
//                 <button
//                   className="relative hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-full"
//                   aria-label="Notifications"
//                   data-notification-toggle
//                   onClick={() => setShowNotifications(!showNotifications)}
//                 >
//                   <i className="fas fa-bell text-gray-500 dark:text-gray-300"></i>
//                   {unreadNotifications > 0 && (
//                     <span className="absolute top-0 right-0 inline-block w-2 h-2 bg-red-500 rounded-full"></span>
//                   )}
//                 </button>

//                 <Notification
//                   isOpen={showNotifications}
//                   onClose={() => setShowNotifications(false)}
//                   onNotificationsRead={() => setUnreadNotifications(0)}
//                 />
//               </div>

//               <button
//                 onClick={() => navigate('/settings')}
//                 className="hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-full"
//                 aria-label="Settings"
//               >
//                 <i className="fas fa-cog text-gray-500 dark:text-gray-300"></i>
//               </button>

//               <button
//                 onClick={toggleTheme}
//                 className="hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-full"
//                 aria-label="Toggle theme"
//               >
//                 {theme === 'light' ? (
//                   <i className="fas fa-moon text-gray-500 dark:text-gray-400"></i>
//                 ) : (
//                   <i className="fas fa-sun text-yellow-500"></i>
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     </header>
//   );
// };

// export default Header;
